import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Container,
  IconButton,
  Paper,
  Typography,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';

import { getUserTypeConfig } from '../config/UserTypes';
import FormField from '../components/patients/FormField';

const buildInitialFormData = (fields, source) => {
  const initial = {};
  fields.forEach((field) => {
    if (field.getInitialValue) {
      initial[field.name] =
        field.getInitialValue(source) ?? field.defaultValue ?? '';
      return;
    }
    const sourceKey = field.initFrom ?? field.name;
    const sourceValue = source?.[sourceKey];
    initial[field.name] = sourceValue ?? field.defaultValue ?? '';
  });
  return initial;
};

const SetUserData = ({ userType }) => {
  const cfg = getUserTypeConfig(userType);
  const navigate = useNavigate();
  const { userId } = useParams();
  const isNew = !userId;

  const [formData, setFormData] = useState(() => buildInitialFormData(cfg.formFields, null));
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(!isNew);
  const [submitting, setSubmitting] = useState(false);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    if (isNew) return;

    const load = async () => {
      setLoading(true);
      setLoadError(null);
      try {
        const data = await cfg.service.getById(userId);
        if (!data || !cfg.roles.includes(data.role)) {
          throw new Error(cfg.labels.loadError);
        }
        setFormData(buildInitialFormData(cfg.formFields, data));
      } catch (e) {
        setLoadError(e.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [cfg, userId, isNew]);

  const onFieldChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined, submitForm: undefined }));
  };

  const validate = () => {
    const next = {};

    cfg.validate?.(formData, next);

    cfg.formFields.forEach((field) => {
      if (!field.required || next[field.name]) return;
      const v = formData[field.name];
      if (v === undefined || v === null || v === '') {
        next[field.name] = 'Campo obligatorio';
      }
    });

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    setErrors((prev) => ({ ...prev, submitForm: undefined }));
    try {
      const payload = {};
      cfg.formFields.forEach((field) => {
        if (field.excludeFromPayload) return;
        const v = formData[field.name];
        payload[field.name] = typeof v === 'string' ? v.trim() : v;
      });

      if (cfg.applyPayloadExtras) {
        Object.assign(payload, cfg.applyPayloadExtras(formData));
      }

      if (isNew) {
        const newId = await cfg.service.add(payload);
        navigate(cfg.routes.view(newId));
      } else {
        await cfg.service.update(userId, payload);
        navigate(cfg.routes.view(userId));
      }
    } catch (e) {
      setErrors((prev) => ({ ...prev, submitForm: e.message || 'Error al guardar' }));
      setSubmitting(false);
    }
  };

  const handleBack = () => navigate(-1);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (loadError) {
    return (
      <Container sx={{ pt: 4 }}>
        <Typography color="error">{loadError}</Typography>
        <Button sx={{ mt: 2 }} onClick={() => navigate(cfg.routes.list())}>
          {cfg.labels.backToList}
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ pt: 4, pb: 8 }}>
      <Paper elevation={3} sx={{ padding: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <IconButton onClick={handleBack} aria-label="back">
            <ArrowBack color="primary" sx={{ fontSize: 40 }} />
          </IconButton>
          <Typography variant="h5" align="center" sx={{ flexGrow: 1 }}>
            {isNew ? cfg.labels.addTitle : cfg.labels.editTitle}
          </Typography>
        </Box>

        <Box component="form" onSubmit={handleSubmit} noValidate>
          {cfg.formFields.map((field) => (
            <FormField
              key={field.name}
              label={field.label}
              name={field.name}
              type={field.type}
              value={formData[field.name] ?? ''}
              onChange={onFieldChange}
              error={errors[field.name]}
              options={field.options}
            />
          ))}

          {errors.submitForm && (
            <Typography color="error" sx={{ mt: 2 }}>
              {errors.submitForm}
            </Typography>
          )}

          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button onClick={handleBack} disabled={submitting}>
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={submitting}
              sx={{ minWidth: 140 }}
            >
              {submitting ? (
                <CircularProgress size={24} color="inherit" />
              ) : isNew ? (
                cfg.labels.createSubmit
              ) : (
                'Guardar'
              )}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default SetUserData;
